import DOMPurify from "dompurify";

/**
 * Sanitise an Azure DevOps rich-text field (Description, Repro Steps,
 * Acceptance Criteria, System Info) for display (PI-SPEC-SW-1362).
 *
 * ADO HTML is user-authored and untrusted. Formatting tags survive; scripts,
 * styles, event handlers, forms and iframes do not. Images are removed
 * (fail-closed): ADO attachment URLs need the user's PAT, which the browser
 * never holds, so they could not load anyway. Links keep only http(s) and
 * mailto targets and open in a new tab without an opener.
 *
 * Returns `null` when nothing visible is left, so callers can show a
 * "not provided" placeholder.
 */
export function sanitizeAdoHtml(html: string): string | null {
  const clean = DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [
      "p", "br", "b", "i", "em", "strong", "u", "s", "strike", "sub", "sup",
      "a", "ul", "ol", "li", "blockquote", "pre", "code",
      "table", "thead", "tbody", "tr", "th", "td",
      "h1", "h2", "h3", "h4", "h5", "h6", "div", "span", "hr",
    ],
    ALLOWED_ATTR: ["href", "title", "colspan", "rowspan"],
    ALLOW_DATA_ATTR: false,
  });

  const template = document.createElement("template");
  template.innerHTML = clean;

  for (const a of template.content.querySelectorAll("a")) {
    const href = a.getAttribute("href") ?? "";
    let safe = false;
    try {
      const url = new URL(href, "https://invalid.invalid/");
      safe = url.protocol === "https:" || url.protocol === "http:" || url.protocol === "mailto:";
      safe = safe && !url.href.startsWith("https://invalid.invalid/");
    } catch {
      safe = false;
    }
    if (safe) {
      a.setAttribute("target", "_blank");
      a.setAttribute("rel", "noopener noreferrer");
    } else {
      a.removeAttribute("href");
    }
  }

  const result = template.innerHTML;
  return template.content.textContent.trim() === "" ? null : result;
}
